/**
 * Shared logging contract for every app in the monorepo.
 * Anonymity-first: known PII keys are redacted before anything is written.
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogContext {
  [key: string]: unknown;
}

export interface ILogger {
  debug(message: string, context?: LogContext): void;
  info(message: string, context?: LogContext): void;
  warn(message: string, context?: LogContext): void;
  error(message: string, context?: LogContext): void;
  child(bindings: LogContext): ILogger;
}

export const DEFAULT_REDACT_KEYS = [
  'email', 'password', 'token', 'accessToken', 'refreshToken',
  'authorization', 'phone', 'name', 'firstName', 'lastName', 'ip',
  'displayName', 'pseudonym', 'emergencyContact',
] as const;

const LEVEL_ORDER: Record<LogLevel, number> = { debug: 10, info: 20, warn: 30, error: 40 };

export function redact(context: LogContext, keys: readonly string[] = DEFAULT_REDACT_KEYS): LogContext {
  const lower = new Set(keys.map((k) => k.toLowerCase()));
  const walk = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([k, v]) =>
          lower.has(k.toLowerCase()) ? [k, '[REDACTED]'] : [k, walk(v)],
        ),
      );
    }
    return value;
  };
  return walk(context) as LogContext;
}

export interface ConsoleLoggerOptions {
  level?: LogLevel;
  bindings?: LogContext;
  redactKeys?: readonly string[];
  /** injectable for tests */
  sink?: (level: LogLevel, payload: Record<string, unknown>) => void;
}

export class ConsoleLogger implements ILogger {
  private readonly level: LogLevel;
  private readonly bindings: LogContext;
  private readonly redactKeys: readonly string[];
  private readonly sink: (level: LogLevel, payload: Record<string, unknown>) => void;

  constructor(options: ConsoleLoggerOptions = {}) {
    this.level = options.level ?? 'info';
    this.bindings = options.bindings ?? {};
    this.redactKeys = options.redactKeys ?? DEFAULT_REDACT_KEYS;
    this.sink =
      options.sink ??
      ((level, payload) => {
        console[level === 'debug' ? 'log' : level](JSON.stringify(payload));
      });
  }

  private log(level: LogLevel, message: string, context?: LogContext): void {
    if (LEVEL_ORDER[level] < LEVEL_ORDER[this.level]) return;
    this.sink(level, {
      level,
      time: new Date().toISOString(),
      msg: message,
      ...redact({ ...this.bindings, ...context }, this.redactKeys),
    });
  }

  debug(m: string, c?: LogContext) { this.log('debug', m, c); }
  info(m: string, c?: LogContext) { this.log('info', m, c); }
  warn(m: string, c?: LogContext) { this.log('warn', m, c); }
  error(m: string, c?: LogContext) { this.log('error', m, c); }

  child(bindings: LogContext): ILogger {
    return new ConsoleLogger({
      level: this.level,
      bindings: { ...this.bindings, ...bindings },
      redactKeys: this.redactKeys,
      sink: this.sink,
    });
  }
}
